import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import Hls from 'hls.js';
import DetailPageLayout from '@/components/layout/DetailPageLayout';
import Breadcrumb from '@/components/common/Breadcrumb';
import ConfirmModal from '@/components/common/ConfirmModal';
import CommentSection from '@/components/feature/CommentSection';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { useLearningCategoryStore, useAssetRenewStore } from '@/stores';
import {
  fetchLearningResourceDetail,
  fetchLearningResourceRecommendations,
  favoriteLearningResource,
  unfavoriteLearningResource,
  unlockLearningContent,
  accessLesson,
  requestLessonPlayAuth,
  fetchLessonManifestWithSession,
  requestEmbeddedVideoPlayAuth,
  fetchEmbeddedVideoManifestData,
  fetchResourceAssetAccess,
  heartbeatPlaySession,
  endPlaySession,
} from '@/service/api';

const levelLabel: Record<string, string> = {
  BEGINNER: '入门',
  INTERMEDIATE: '进阶',
  ADVANCED: '高级',
  beginner: '入门',
  intermediate: '进阶',
  advanced: '高级'
};

const levelColor: Record<string, string> = {
  BEGINNER: 'bg-teal-50 text-teal-700 border-teal-200',
  INTERMEDIATE: 'bg-amber-50 text-amber-700 border-amber-200',
  ADVANCED: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  beginner: 'bg-teal-50 text-teal-700 border-teal-200',
  intermediate: 'bg-amber-50 text-amber-700 border-amber-200',
  advanced: 'bg-indigo-50 text-indigo-700 border-indigo-200'
};

const defaultCover = 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&auto=format&fit=crop&q=60';

function formatDuration(seconds?: number): string {
  if (!seconds) return '暂无时长';
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}分钟`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return remainingMins > 0 ? `${hours}小时${remainingMins}分` : `${hours}小时`;
}

function cleanManifest(manifest?: string): string {
  if (!manifest) return '';
  return manifest.replace(/\\u0026/g, '&').replace(/&#38;/g, '&');
}

function getDeviceID(): string {
  let id = localStorage.getItem('learning-device-id');
  if (!id) {
    id = typeof window !== 'undefined' && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem('learning-device-id', id);
  }
  return id;
}

function processAndRenderRichText(
  container: HTMLElement,
  html: string,
  assets?: Record<string, Api.Learning.RichTextAssetResponse>,
  options: { isCourse?: boolean } = {}
) {
  container.className = `learning-content ${options.isCourse ? 'learning-content--course' : 'learning-content--lesson'}`;
  container.innerHTML = html || '<p class="content-paragraph content-text--muted">暂无内容。</p>';

  container.querySelectorAll('script, iframe, object, embed, audio').forEach((node) => node.remove());
  container.querySelectorAll('*').forEach((node) => {
    Array.from(node.attributes).forEach((attr) => {
      if (/^on/i.test(attr.name) || attr.name === 'style') {
        node.removeAttribute(attr.name);
      }
    });
  });

  if (options.isCourse) {
    container.querySelectorAll('video').forEach((node) => node.remove());
  }

  container.querySelectorAll<HTMLImageElement>('img[data-asset-id]').forEach((img) => {
    const assetId = img.dataset.assetId;
    if (assetId && assets?.[assetId]) {
      const asset = assets[assetId];
      const assetUrl = asset.url || (asset as any).access_url;
      if (assetUrl) {
        img.src = assetUrl;
        img.dataset.expiresAt = String(asset.expires_at || (asset as any).access_expires || 0);
        img.loading = 'lazy';
        img.classList.add('content-image');
      } else {
        img.replaceWith(document.createTextNode('[图片暂不可用]'));
      }
    } else if (assetId) {
      img.replaceWith(document.createTextNode('[图片暂不可用]'));
    }
  });

  container.querySelectorAll<HTMLImageElement>('img:not([data-asset-id])').forEach((img) => {
    img.classList.add('content-image');
  });
}

async function refreshResourceAssets(
  container: HTMLElement,
  resourceId: string | number,
  richTextAssets?: Record<string, Api.Learning.RichTextAssetResponse>
) {
  const images = Array.from(container.querySelectorAll<HTMLImageElement>('img[data-asset-id]'));
  if (!images.length || !resourceId) return;

  // 1. 将 DOM 中渲染的图片及其 expires_at 显式写入 Store 缓存
  images.forEach((img) => {
    const assetId = img.dataset.assetId;
    if (!assetId) return;

    let url = img.src;
    let expires: number | undefined = undefined;

    if (img.dataset.expiresAt && img.dataset.expiresAt !== '0') {
      expires = Number(img.dataset.expiresAt);
    }

    if (richTextAssets?.[assetId]) {
      const asset = richTextAssets[assetId];
      url = asset.url || (asset as any).access_url || url;
      expires = asset.expires_at || (asset as any).access_expires || expires;
    }

    if (url) {
      useAssetRenewStore.getState().setAssetCacheItem(String(assetId), url, expires);
    }
  });

  const assetIds = Array.from(new Set(images.map((img) => img.dataset.assetId).filter(Boolean))) as string[];

  try {
    // 2. 课程详情与课时内容：使用专属授权接口 POST /learning/resources/{id}/asset-access（仅到期续发）
    const items = await useAssetRenewStore.getState().renewResourceAssets(resourceId, assetIds, { onlyExpired: true });

    items.forEach((asset) => {
      if (!asset.asset_id || !asset.url) return;
      container
        .querySelectorAll<HTMLImageElement>(`img[data-asset-id="${CSS.escape(String(asset.asset_id))}"]`)
        .forEach((img) => {
          img.src = asset.url!;
        });
    });
  } catch (err: any) {
    console.warn('学习资源图片授权续签失败:', err);
  }
}

async function loadEmbeddedVideos(root: HTMLElement) {
  const targets = Array.from(root.querySelectorAll<HTMLVideoElement>('video[data-embedded-video-id]'));
  await Promise.all(
    targets.map(async (element) => {
      const embeddedVideoID = element.dataset.embeddedVideoId;
      if (!embeddedVideoID) return;

      element.classList.add('content-video');
      element.controls = true;
      element.playsInline = true;
      element.preload = 'metadata';

      try {
        let manifestText = '';
        try {
          // 优先通过 useAssetRenewStore 调用 requestEmbeddedVideoPlayAuth 续期或申请授权
          const renewEmbeddedVideoAuth = useAssetRenewStore.getState().renewEmbeddedVideoAuth;
          const authRes = await renewEmbeddedVideoAuth(embeddedVideoID);
          manifestText = authRes.manifest || '';
        } catch {
          // 备用兜底逻辑：获取 Manifest 字符串
          manifestText = await fetchEmbeddedVideoManifestData(embeddedVideoID);
        }

        if (!manifestText) {
          throw new Error('未取得视频播放授权清单');
        }

        const cleaned = cleanManifest(manifestText);
        const blobUrl = URL.createObjectURL(new Blob([cleaned], { type: 'application/vnd.apple.mpegurl' }));

        if (Hls.isSupported()) {
          const hls = new Hls({ enableWorker: true });
          hls.loadSource(blobUrl);
          hls.attachMedia(element);
        } else if (element.canPlayType('application/vnd.apple.mpegurl')) {
          element.src = blobUrl;
        } else {
          throw new Error('当前浏览器不支持 HLS 视频播放。');
        }
      } catch (error: any) {
        element.insertAdjacentHTML(
          'afterend',
          `<p class="content-paragraph content-text--danger">嵌入视频暂不可播放：${error?.message || '加载失败'}</p>`
        );
        element.remove();
      }
    })
  );
}

export default function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoggedIn, rawUser } = useAuth();
  const loginAs = useAuthStore((state) => state.loginAs);
  const fetchCurrentAccount = useAuthStore((state) => state.fetchCurrentAccount);
  const fetchCategories = useLearningCategoryStore((state) => state.fetchCategories);
  const getCategoryNameByCode = useLearningCategoryStore((state) => state.getCategoryNameByCode);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 离开当前页面或切换课程时，自动清空当前页面的续签与授权缓存
  useEffect(() => {
    return () => {
      useAssetRenewStore.getState().clearCache();
    };
  }, [id]);


  const [detailData, setDetailData] = useState<Api.Learning.ResourceDetailResponse | null>(null);
  const [recommendations, setRecommendations] = useState<Api.Learning.ResourceResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorited, setIsFavorited] = useState<boolean>(false);
  const [favoriteLoading, setFavoriteLoading] = useState<boolean>(false);
  const [unlockModalType, setUnlockModalType] = useState<'confirm' | 'insufficient' | null>(null);
  const [unlockLoading, setUnlockLoading] = useState<boolean>(false);
  const [unlockVersion, setUnlockVersion] = useState<number>(0);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const [activeLessonId, setActiveLessonId] = useState<string>('');

  const courseDescriptionRef = useRef<HTMLDivElement | null>(null);
  const assetRenewalTimerRef = useRef<any>(null);
  const fetchedDetailIdRef = useRef<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!id || fetchedDetailIdRef.current === id) return;
    fetchedDetailIdRef.current = id;
    setLoading(true);
    setError(null);
    try {
      const [data, recs] = await Promise.all([
        fetchLearningResourceDetail(id),
        fetchLearningResourceRecommendations(id).catch((err) => {
          console.error('获取推荐资源失败', err);
          return [];
        })
      ]);
      setDetailData(data);
      setIsFavorited(!!data?.resource?.is_favorited);
      setRecommendations(recs || []);
      if (data?.lessons?.length) {
        const urlLessonId = searchParams.get('lessonId') || searchParams.get('lesson');
        const matched = data.lessons.find(
          (l) => l.id === urlLessonId || String(l.lesson_no) === urlLessonId
        );
        const targetLesson = matched || data.lessons[0];
        setActiveLessonId(targetLesson.id);
        if (!urlLessonId) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set('lessonId', targetLesson.id);
              return next;
            },
            { replace: true }
          );
        }
      }
    } catch (err: any) {
      console.error('获取学习资源详情失败', err);
      fetchedDetailIdRef.current = null;
      setError(err?.message || '获取课程详情失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [id, searchParams, setSearchParams]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (!detailData?.lessons?.length) return;
    const urlLessonId = searchParams.get('lessonId') || searchParams.get('lesson');
    if (urlLessonId && !activeLessonId) {
      const matched = detailData.lessons.find(
        (l) => l.id === urlLessonId || String(l.lesson_no) === urlLessonId
      );
      if (matched) {
        setActiveLessonId(matched.id);
      }
    }
  }, [searchParams, detailData?.lessons, activeLessonId]);

  const handleSelectLesson = useCallback(
    (lessonId: string) => {
      setActiveLessonId(lessonId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('lessonId', lessonId);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const assetCache = useAssetRenewStore((state) => state.assetCache);

  // 监听课程详情数据，自动集中预存封面及介绍 (rich_text_assets) 的凭证，并注册 LEARNING_RESOURCE 上下文发起续期
  useEffect(() => {
    if (!detailData || !id) return;

    const coverAsset = detailData.cover_asset || detailData.resource?.cover_asset;
    const coverAssetId = coverAsset?.id || (detailData as any)?.cover_asset_id;
    const coverUrl = coverAsset?.access_url;
    const coverExpires = coverAsset?.access_expires;

    if (coverAssetId && coverUrl) {
      useAssetRenewStore.getState().setAssetCacheItem(String(coverAssetId), coverUrl, coverExpires);
    }

    const richTextAssets = detailData.rich_text_assets;
    const richTextAssetIds: string[] = [];
    if (richTextAssets) {
      const assetList = Array.isArray(richTextAssets) ? richTextAssets : Object.values(richTextAssets);
      assetList.forEach((asset: any) => {
        const assetId = asset.asset_id || asset.id;
        const url = asset.access_url || asset.url;
        const expires = asset.access_expires || asset.expires_at;
        if (assetId) {
          richTextAssetIds.push(String(assetId));
          if (url) {
            useAssetRenewStore.getState().setAssetCacheItem(String(assetId), url, expires);
          }
        }
      });
    }

    // 仅课程封面使用 LEARNING_RESOURCE 通用 OSS 资源类型
    if (coverAssetId) {
      useAssetRenewStore.getState().renewBatchResources([
        {
          resource_type: 'LEARNING_RESOURCE',
          resource_id: id,
          asset_ids: [String(coverAssetId)],
        },
      ]).catch(() => { });
    }
  }, [detailData, id]);

  useEffect(() => {
    if (!courseDescriptionRef.current || !detailData?.description) return;
    const container = courseDescriptionRef.current;
    processAndRenderRichText(container, detailData.description, detailData.rich_text_assets, { isCourse: true });

    if (id) {
      refreshResourceAssets(container, id);
    }
  }, [detailData?.description, detailData?.rich_text_assets, id]);

  // 当 assetCache 变更时，更新课程介绍 DOM 中对应图片的 src
  useEffect(() => {
    if (!courseDescriptionRef.current) return;
    const container = courseDescriptionRef.current;
    const images = container.querySelectorAll<HTMLImageElement>('img[data-asset-id]');
    images.forEach((img) => {
      const assetId = img.dataset.assetId;
      if (assetId && assetCache[assetId]?.url && img.src !== assetCache[assetId].url) {
        img.src = assetCache[assetId].url;
      }
    });
  }, [assetCache]);

  const handleToggleFavorite = async () => {
    if (!id || favoriteLoading) return;
    setFavoriteLoading(true);
    try {
      if (isFavorited) {
        await unfavoriteLearningResource(id);
        setIsFavorited(false);
      } else {
        await favoriteLearningResource(id);
        setIsFavorited(true);
      }
    } catch (err) {
      console.error('收藏状态更新失败', err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  const showToast = useCallback((message: string) => {
    setToastMsg(message);
    setTimeout(() => {
      setToastMsg(null);
    }, 2500);
  }, []);

  const isVipLocked = detailData?.access_state === 'LOCKED' || detailData?.access_state === 'LOGIN_REQUIRED';
  const resource = detailData?.resource;
  const isPurchasedOrUnlocked = Boolean(resource?.is_purchased || !isVipLocked);
  const isVideoCourse = resource?.resource_type === 'VIDEO';
  const pointsPrice = resource?.points_price ?? 0;
  const totalPoints = rawUser?.membership?.wallet?.total_balance ?? 0;
  const lessons = detailData?.lessons || [];

  const activeLessonData = lessons.find((l) => l.id === activeLessonId) || lessons[0];

  const handleUnlockClick = useCallback(() => {
    if (!id || unlockLoading) return;
    if (!isLoggedIn) {
      navigate('/auth');
      return;
    }
    if (totalPoints < pointsPrice) {
      setUnlockModalType('insufficient');
      return;
    }
    setUnlockModalType('confirm');
  }, [id, isLoggedIn, navigate, pointsPrice, totalPoints, unlockLoading]);

  const handleCloseUnlockModal = useCallback(() => {
    if (unlockLoading) return;
    setUnlockModalType(null);
  }, [unlockLoading]);

  const handleConfirmUnlock = useCallback(async () => {
    if (!id || unlockLoading) return;
    if (unlockModalType === 'insufficient') {
      setUnlockModalType(null);
      navigate('/membership');
      return;
    }
    setUnlockLoading(true);
    try {
      const res = await unlockLearningContent(id, { idempotency_key: '' });
      const membershipSpent = res.membership_point_spent ?? 0;
      const purchasedSpent = res.purchased_point_spent ?? 0;
      const totalSpent = membershipSpent + purchasedSpent;
      if (rawUser) {
        const wallet = rawUser.membership?.wallet;
        loginAs({
          ...rawUser,
          membership: {
            ...rawUser.membership,
            wallet: {
              ...wallet,
              membership_balance: Math.max(0, (wallet?.membership_balance ?? 0) - membershipSpent),
              purchased_balance: Math.max(0, (wallet?.purchased_balance ?? 0) - purchasedSpent),
              total_balance: Math.max(0, (wallet?.total_balance ?? 0) - totalSpent)
            }
          }
        });
      }
      setDetailData((prev) => (prev ? { ...prev, access_state: 'ACCESSIBLE' } : prev));
      setUnlockVersion((prev) => prev + 1);
      setUnlockModalType(null);
      if (res.already_unlocked) {
        showToast('课程已解锁，可继续学习');
      } else {
        showToast(totalSpent > 0 ? `解锁成功，已扣除 ${totalSpent} 积分` : '课程解锁成功');
      }
      fetchCurrentAccount().catch((err) => {
        console.warn('刷新账号积分失败', err);
      });
    } catch (err: any) {
      console.error('解锁课程失败', err);
      showToast(err?.message || '解锁失败，请稍后重试');
    } finally {
      setUnlockLoading(false);
    }
  }, [fetchCurrentAccount, id, loginAs, navigate, rawUser, showToast, unlockLoading, unlockModalType]);

  if (loading) {
    return (
      <DetailPageLayout bgClass="bg-slate-50">
        <div className="pt-20 sm:pt-24 detail-shell py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded-2xl w-full"></div>
            <div className="h-32 bg-gray-200 rounded-2xl w-full"></div>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  if (error || !detailData || !resource) {
    return (
      <DetailPageLayout bgClass="bg-slate-50">
        <div className="pt-20 sm:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-950 mb-2">{error || '课程未找到'}</h2>
            <Link to="/courses" className="text-sm text-accent-600 hover:text-accent-500 cursor-pointer">
              返回学习大厅
            </Link>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  const coverAsset = detailData.cover_asset || resource?.cover_asset;
  const coverAssetId = coverAsset?.id || (detailData as any)?.cover_asset_id || (resource as any)?.cover_asset_id;
  const coverUrl =
    (coverAssetId && assetCache[String(coverAssetId)]?.url) ||
    coverAsset?.access_url ||
    defaultCover;
  const difficulty = resource?.difficulty || 'BEGINNER';

  return (
    <DetailPageLayout
      bgClass="bg-slate-50"
      sidebarWidth="lg:w-[320px] xl:w-[350px]"
      header={
        <div className="pt-16 sm:pt-20">
          <div className="detail-shell">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '学习大厅', href: '/courses' },
                { label: resource.title },
              ]}
              extra={
                <button
                  onClick={handleToggleFavorite}
                  disabled={favoriteLoading}
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer border ${isFavorited
                    ? 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                >
                  <i className={isFavorited ? 'ri-heart-fill text-rose-500' : 'ri-heart-line'}></i>
                  <span>{isFavorited ? '已收藏' : '收藏'}</span>
                </button>
              }
            />
          </div>
        </div>
      }
      sidebar={
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <div>
              <h2 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <i className="ri-menu-2-line text-teal-600"></i>
                课时目录
              </h2>
            </div>
            <span className="text-xs text-slate-400 font-medium">共 {lessons.length} 节</span>
          </div>

          <div className="max-h-[calc(100vh-280px)] overflow-y-auto divide-y divide-slate-100">
            {lessons.map((lesson, index) => {
              const isActive = activeLessonId === lesson.id;
              const isLocked = isVipLocked && !lesson.preview_enabled;

              return (
                <button
                  key={lesson.id || index}
                  type="button"
                  onClick={() => lesson.id && handleSelectLesson(lesson.id)}
                  className={`w-full flex items-start gap-3 px-4 py-3.5 text-left transition-all cursor-pointer ${isActive
                    ? 'bg-teal-50/80 border-l-4 border-l-teal-600'
                    : 'hover:bg-slate-50 border-l-4 border-l-transparent'
                    }`}
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 transition-colors ${isActive
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-100 text-slate-500'
                      }`}
                  >
                    {lesson.lesson_no || index + 1}
                  </span>

                  <div className="flex-1 min-w-0">
                    <p className={`text-xs leading-snug line-clamp-2 ${isActive ? 'text-slate-900 font-bold' : 'text-slate-700'}`}>
                      {lesson.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-slate-400">
                        {formatDuration(lesson.estimated_duration_seconds)}
                      </span>
                      {lesson.preview_enabled && !isPurchasedOrUnlocked ? (
                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-teal-100 text-teal-800 font-medium">试看</span>
                      ) : isLocked ? (
                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 font-medium flex items-center gap-0.5">
                          <i className="ri-lock-line text-[10px]"></i>需解锁
                        </span>
                      ) : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {isVipLocked && (
            <div className="p-4 bg-amber-50/60 border-t border-amber-100">
              <p className="text-xs text-amber-800 mb-2.5 text-center font-medium">完整课程需解锁后观看</p>
              {isLoggedIn ? (
                <button
                  type="button"
                  onClick={handleUnlockClick}
                  disabled={unlockLoading}
                  className="w-full py-2 px-3 rounded-lg bg-amber-400 text-slate-950 text-xs font-bold hover:bg-amber-300 transition-all cursor-pointer shadow-xs border border-amber-300 disabled:opacity-60"
                >
                  {unlockLoading ? '解锁中...' : `使用 ${pointsPrice} 积分解锁`}
                </button>
              ) : (
                <Link
                  to="/auth"
                  className="block w-full text-center py-2 px-3 rounded-lg bg-amber-400 text-slate-950 text-xs font-bold hover:bg-amber-300 transition-all cursor-pointer shadow-xs border border-amber-300"
                >
                  登录后解锁课程
                </Link>
              )}
            </div>
          )}
        </div>
      }
      extra={
        recommendations.length > 0 ? (
          <section className="pt-4 pb-4">
            <h2 className="font-bold text-lg text-slate-900 mb-5">推荐课程</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {recommendations.map((sc) => {
                const recCover = sc.cover_asset?.access_url || defaultCover;
                return (
                  <Link
                    key={sc.id}
                    to={`/courses/${sc.id}`}
                    className="rounded-2xl overflow-hidden border border-slate-200/80 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer group bg-white"
                  >
                    <div className="relative h-36 overflow-hidden bg-slate-100">
                      <img
                        src={recCover}
                        alt={sc.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      {sc.access_mode === 'POINTS' && (
                        <div className="absolute top-2.5 right-2.5">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400 text-slate-950 font-bold">VIP/积分</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-slate-900 text-sm leading-snug line-clamp-2 mb-2">
                        {sc.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>{formatDuration(sc.estimated_duration_seconds)} &middot; {sc.lesson_count || 0}课时</span>
                        {sc.difficulty && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                            {levelLabel[sc.difficulty] || sc.difficulty}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ) : null
      }
      comments={
        id ? (
          <CommentSection
            targetType="LEARNING_RESOURCE"
            targetId={id}
            title="课程评价与问答"
            isTargetAuthor={Boolean(
              isLoggedIn &&
              rawUser &&
              ((detailData?.resource as any)?.author_account_id
                ? rawUser.id === (detailData?.resource as any).author_account_id
                : false)
            )}
          />
        ) : null
      }
      modals={
        <>
          <ConfirmModal
            open={Boolean(unlockModalType)}
            title={unlockModalType === 'insufficient' ? '积分不足，暂无法解锁' : '确认解锁当前课程？'}
            content={
              unlockModalType === 'insufficient' ? (
                <div className="space-y-2 text-left">
                  <p>当前课程解锁需要消耗 {pointsPrice} 积分。</p>
                  <p>你当前可用积分为 {totalPoints}，暂不足以完成解锁。</p>
                </div>
              ) : (
                <div className="space-y-2 text-left">
                  <p>解锁后可访问当前课程的全部学习内容。</p>
                  <p>本次预计消耗 {pointsPrice} 积分，当前可用积分 {totalPoints}。</p>
                </div>
              )
            }
            type={unlockModalType === 'insufficient' ? 'info' : 'warning'}
            confirmText={unlockModalType === 'insufficient' ? '去充值积分' : '确认解锁'}
            cancelText="取消"
            onConfirm={handleConfirmUnlock}
            onCancel={handleCloseUnlockModal}
            loading={unlockModalType === 'confirm' ? unlockLoading : false}
          />

          {toastMsg && (
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl bg-slate-900/90 text-white text-sm font-medium shadow-lg backdrop-blur-md flex items-center gap-2 animate-fade-in">
              <i className="ri-checkbox-circle-fill text-emerald-400 text-base"></i>
              <span>{toastMsg}</span>
            </div>
          )}
        </>
      }
    >
      {/* Course Header */}
      <section className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-xs">
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="w-full sm:w-44 h-32 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 relative border border-slate-200/60">
            <img src={coverUrl} alt={resource.title} className="w-full h-full object-cover" />
            {resource.is_purchased ? (
              <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded bg-emerald-500 text-white font-bold shadow-xs">
                已购买
              </span>
            ) : resource.access_mode === 'POINTS' ? (
              <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded bg-amber-400 text-slate-950 font-bold shadow-xs">
                {resource.points_price ? `${resource.points_price} 积分` : 'VIP积分'}
              </span>
            ) : null}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] px-2.5 py-0.5 rounded-md font-bold bg-teal-50 text-teal-700 border border-teal-200/60">
                {isVideoCourse ? '视频课程' : '图文/文章课程'}
              </span>
              <span className={`text-[11px] px-2.5 py-0.5 rounded-md font-medium border ${levelColor[difficulty] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                {levelLabel[difficulty] || difficulty}
              </span>
            </div>

            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug mb-2">
              {resource.title}
            </h1>

            {resource.subtitle && (
              <p className="text-xs sm:text-sm text-slate-500 line-clamp-2 mb-3">
                {resource.subtitle}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 pt-1 border-t border-slate-100">
              <span>{resource.lesson_count || lessons.length || 0} 个课时</span>
              <span>&middot;</span>
              <span>预计 {formatDuration(resource.estimated_duration_seconds)}</span>
              {resource.category_code && (
                <>
                  <span>&middot;</span>
                  <span>{getCategoryNameByCode(resource.category_code, resource.category_code)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Active Lesson Content */}
      <section className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <div className="min-w-0">
            <span className="text-xs font-bold text-teal-700 uppercase tracking-wider">
              {activeLessonData?.content_type === 'VIDEO' ? '视频课时' : '图文课时'}
            </span>
            <h2 className="text-base font-bold text-slate-900 truncate mt-0.5">
              {activeLessonData ? `第 ${activeLessonData.lesson_no || 1} 课 · ${activeLessonData.title}` : '课时内容'}
            </h2>
          </div>
          {activeLessonData && (
            <span className="text-xs text-slate-400 flex-shrink-0">
              {formatDuration(activeLessonData.estimated_duration_seconds)}
            </span>
          )}
        </div>

        <div className="p-6">
          <LessonStageContent
            resourceId={id || ''}
            lesson={activeLessonData}
            accessState={detailData?.access_state}
            isVipLocked={isVipLocked}
            isLoggedIn={isLoggedIn}
            pointsPrice={pointsPrice}
            unlockLoading={unlockLoading}
            unlockVersion={unlockVersion}
            onUnlockCourse={handleUnlockClick}
          />
        </div>
      </section>

      {/* Course Description */}
      {detailData.description && (
        <section className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200/80 shadow-xs">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <i className="ri-article-line text-teal-600"></i>
                课程详情
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">课程介绍、学习收获与相关说明</p>
            </div>
          </div>
          <div ref={courseDescriptionRef} className="learning-content learning-content--course w-full" />
        </section>
      )}
    </DetailPageLayout>
  );
}

function LessonStageContent({
  resourceId,
  lesson,
  accessState,
  isVipLocked,
  isLoggedIn,
  pointsPrice,
  unlockLoading,
  unlockVersion,
  onUnlockCourse
}: {
  resourceId: string | number;
  lesson?: Api.Learning.LessonResponse;
  accessState?: string;
  isVipLocked: boolean;
  isLoggedIn: boolean;
  pointsPrice: number;
  unlockLoading: boolean;
  unlockVersion: number;
  onUnlockCourse: () => void;
}) {
  const [accessCache, setAccessCache] = useState<Record<string, Api.Learning.LessonAccessResponse>>({});
  const [accessErrorCache, setAccessErrorCache] = useState<
    Record<string, { message: string; code?: number; errorKey?: string }>
  >({});
  const accessCacheRef = useRef<Record<string, Api.Learning.LessonAccessResponse>>({});
  const accessErrorCacheRef = useRef<Record<string, { message: string; code?: number; errorKey?: string }>>({});
  const pendingLessonIdsRef = useRef<Set<string>>(new Set());

  const [manifest, setManifest] = useState<string>('');
  const [videoError, setVideoError] = useState<string | null>(null);
  const [playerNote, setPlayerNote] = useState<string>('正在准备播放授权…');

  const sessionIDRef = useRef<string>('');
  const expiresAtRef = useRef<number>(0);
  const heartbeatTimerRef = useRef<any>(null);

  const articleContentRef = useRef<HTMLDivElement | null>(null);
  const assetRenewalTimerRef = useRef<any>(null);

  const lessonId = lesson?.id;
  const isVideoLesson = lesson?.content_type === 'VIDEO';

  const currentAccessData = lessonId ? accessCache[lessonId] : undefined;
  const currentAccessError = lessonId ? accessErrorCache[lessonId] : undefined;
  const isLessonLocked = currentAccessError?.code === 52003 || currentAccessError?.errorKey === 'learning.resource_locked';

  const isLoginRequired = !isLoggedIn || accessState === 'LOGIN_REQUIRED';
  const isLocked = isLoginRequired || (accessState === 'LOCKED' && !lesson?.preview_enabled) || isLessonLocked;

  // 同步推导当前课时是否正在加载访问权限数据，避免在 useEffect 中 setState(true) 触发二次渲染和二次抖动
  const isLessonLoading = Boolean(!isLocked && lessonId && !currentAccessData && !currentAccessError);

  useEffect(() => {
    if (!unlockVersion) return;
    accessCacheRef.current = {};
    accessErrorCacheRef.current = {};
    pendingLessonIdsRef.current.clear();
    setAccessCache({});
    setAccessErrorCache({});
  }, [unlockVersion]);

  useEffect(() => {
    setManifest('');
    setVideoError(null);
    setPlayerNote('正在准备播放授权…');
  }, [lessonId]);

  useEffect(() => {
    if (!lessonId || isLocked) return;
    if (accessCacheRef.current[lessonId] || accessErrorCacheRef.current[lessonId]) return;
    if (pendingLessonIdsRef.current.has(lessonId)) return;
    pendingLessonIdsRef.current.add(lessonId);

    accessLesson(lessonId)
      .then((res) => {
        if (res?.code === 52003 || res?.error_key === 'learning.resource_locked') {
          const accessError = {
            message: res?.message || res?.msg || '该学习资源需要购买积分解锁后访问',
            code: res?.code,
            errorKey: res?.error_key
          };
          accessErrorCacheRef.current[lessonId] = accessError;
          setAccessErrorCache((prev) => ({ ...prev, [lessonId]: accessError }));
          return;
        }
        if (res?.data) {
          accessCacheRef.current[lessonId] = res.data;
          setAccessCache((prev) => ({ ...prev, [lessonId]: res.data }));
        }
      })
      .catch((err) => {
        console.warn('访问课时失败:', err);
      })
      .finally(() => {
        pendingLessonIdsRef.current.delete(lessonId);
      });
  }, [isLocked, lessonId, unlockVersion]);

  const loadVideoAuth = useCallback(
    async (targetLessonId: string) => {
      setVideoError(null);
      setPlayerNote('正在加载视频播放授权…');
      try {
        const deviceID = getDeviceID();
        const payload = await requestLessonPlayAuth(targetLessonId, {
          'X-Device-ID': deviceID
        });
        let manifestText = cleanManifest(payload?.manifest);
        let newSessionId = payload?.play_session_id || '';
        let expiresAt = Number(payload?.expires_at || 0);
        if (!manifestText) {
          const fallbackRes = await fetchLessonManifestWithSession(targetLessonId, sessionIDRef.current);
          manifestText = cleanManifest(fallbackRes.manifest);
          if (fallbackRes.sessionId) newSessionId = fallbackRes.sessionId;
        }
        if (!manifestText) {
          throw new Error('未获取到有效视频播放 Manifest。');
        }

        sessionIDRef.current = newSessionId;
        expiresAtRef.current = expiresAt;
        setManifest(manifestText);
        setPlayerNote('');
      } catch (err: any) {
        console.error('加载视频播放授权失败:', err);
        setVideoError(err?.message || '视频授权认证失败');
      }
    },
    []
  );

  useEffect(() => {
    if (!isVideoLesson || !lessonId || !currentAccessData || isLocked) return;

    loadVideoAuth(lessonId);

    const heartbeatInterval = setInterval(() => {
      if (sessionIDRef.current) {
        heartbeatPlaySession(sessionIDRef.current).catch((err) => {
          console.warn('播放 Session 心跳失败:', err);
        });
      }
    }, 30000);
    heartbeatTimerRef.current = heartbeatInterval;

    return () => {
      clearInterval(heartbeatTimerRef.current);
      if (sessionIDRef.current) {
        endPlaySession(sessionIDRef.current).catch(() => { });
        sessionIDRef.current = '';
      }
    };
  }, [isVideoLesson, lessonId, currentAccessData, isLocked, loadVideoAuth]);

  const assetCache = useAssetRenewStore((state) => state.assetCache);

  // 当后台巡检 Timer 自动续签更新 Store 的 assetCache 时，同步更新图文课时 DOM 图片的 src
  useEffect(() => {
    if (!articleContentRef.current) return;
    const container = articleContentRef.current;
    const images = container.querySelectorAll<HTMLImageElement>('img[data-asset-id]');
    images.forEach((img) => {
      const assetId = img.dataset.assetId;
      if (assetId && assetCache[assetId]?.url && img.src !== assetCache[assetId].url) {
        img.src = assetCache[assetId].url;
      }
    });
  }, [assetCache]);

  useLayoutEffect(() => {
    if (isVideoLesson || isLocked || !currentAccessData?.article_content || !articleContentRef.current) return;
    const container = articleContentRef.current;
    processAndRenderRichText(
      container,
      currentAccessData.article_content,
      currentAccessData.rich_text_assets,
      { isCourse: false }
    );
    loadEmbeddedVideos(container);

    // 预存接口返回的图文富文本资产初始 access_url 到 Store 缓存
    const richTextAssets = currentAccessData.rich_text_assets;
    if (richTextAssets) {
      if (Array.isArray(richTextAssets)) {
        richTextAssets.forEach((asset: any) => {
          const assetId = asset.asset_id || asset.id;
          const url = asset.url || asset.access_url;
          const expires = asset.expires_at || asset.access_expires;
          if (assetId && url) {
            useAssetRenewStore.getState().setAssetCacheItem(String(assetId), url, expires);
          }
        });
      } else {
        Object.entries(richTextAssets).forEach(([key, asset]: [string, any]) => {
          const assetId = asset.asset_id || asset.id || key;
          const url = asset.url || asset.access_url;
          const expires = asset.expires_at || asset.access_expires;
          if (assetId && url) {
            useAssetRenewStore.getState().setAssetCacheItem(String(assetId), url, expires);
          }
        });
      }
    }

    // 立即注册图文课时包含的所有图片资产上下文到 Store 发起并开启自动续签
    if (resourceId) {
      refreshResourceAssets(container, resourceId, currentAccessData.rich_text_assets);
    }
  }, [isVideoLesson, isLocked, currentAccessData, resourceId]);

  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!manifest || !videoRef.current) return;
    const video = videoRef.current;
    const blobUrl = URL.createObjectURL(new Blob([manifest], { type: 'application/vnd.apple.mpegurl' }));

    let hls: Hls | null = null;
    if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true });
      hls.loadSource(blobUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = blobUrl;
    }

    return () => {
      if (hls) hls.destroy();
      URL.revokeObjectURL(blobUrl);
    };
  }, [manifest]);

  if (isLessonLoading) {
    return (
      <div key="loading" className="min-h-[360px] py-12 flex flex-col items-center justify-center text-slate-400 rounded-xl border border-dashed border-slate-200/80 bg-slate-50/40">
        <i className="ri-loader-4-line text-2xl animate-spin text-teal-600 mb-2"></i>
        <p className="text-xs font-medium text-slate-500">正在加载课时内容...</p>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div key="locked" className="min-h-[360px] py-12 px-6 rounded-2xl bg-amber-50/50 border border-amber-200/80 text-center flex flex-col justify-center items-center">
        <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-3 text-xl">
          <i className={isLoginRequired ? 'ri-user-shared-line' : 'ri-lock-2-line'}></i>
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1">
          {isLoginRequired ? '需要登录后观看' : '当前课时受限'}
        </h3>
        <p className="text-xs text-slate-500 mb-5 max-w-sm mx-auto">
          {isLoginRequired
            ? '请登录账号，即可观看试看资源或解锁完整课程。'
            : '您需要解锁当前课程后即可观看完整课时内容与资源文件。'}
        </p>
        {isLoginRequired ? (
          <Link
            to="/auth"
            className="inline-block px-6 py-2.5 rounded-xl bg-amber-400 text-slate-950 text-xs font-bold hover:bg-amber-300 transition-all cursor-pointer shadow-xs border border-amber-300"
          >
            登录后观看课程
          </Link>
        ) : (
          <button
            type="button"
            onClick={onUnlockCourse}
            disabled={unlockLoading}
            className="px-6 py-2.5 rounded-xl bg-amber-400 text-slate-950 text-xs font-bold hover:bg-amber-300 transition-all cursor-pointer shadow-xs border border-amber-300 disabled:opacity-60"
          >
            {unlockLoading ? '解锁中...' : `使用 ${pointsPrice} 积分解锁完整课程`}
          </button>
        )}
      </div>
    );
  }

  if (isVideoLesson) {
    return (
      <div key={`video-${lessonId}`} className="space-y-4 min-h-[360px]">
        <div className="relative aspect-video rounded-xl bg-slate-950 overflow-hidden flex items-center justify-center border border-slate-800">
          {videoError ? (
            <div className="text-center p-4">
              <i className="ri-error-warning-line text-2xl text-rose-400 mb-1 block"></i>
              <p className="text-xs text-rose-300">{videoError}</p>
            </div>
          ) : manifest ? (
            <video ref={videoRef} controls controlsList="nodownload" playsInline className="w-full h-full object-contain" />
          ) : (
            <div className="text-center p-4 text-slate-400">
              <i className="ri-loader-4-line text-2xl animate-spin text-teal-500 mb-2 block"></i>
              <p className="text-xs">{playerNote}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div key="article" className="w-full min-h-[300px]">
      <div ref={articleContentRef} className="learning-content learning-content--lesson w-full" />
    </div>
  );
}
